/**
 * useTaskTransition — Side-effect hook that fires on task STATUS transitions.
 *
 * Generalization of the useTaskNotifications prev/curr diff pattern: a single
 * transient useTaskStore.subscribe (armed once, outside React render) diffs the
 * previous vs current task map and invokes onTransition ONLY when a matching
 * task's status actually changes. Heartbeat / phase-metadata writes (object
 * identity changes, same status) and non-matching tasks never fire it.
 *
 * Domain surfaces use this instead of homegrown setTimeout refresh ladders:
 *
 *   useTaskTransition({
 *     filter: (task) => task.taskType?.startsWith('ad-creative') ?? false,
 *     onTransition: (task) => {
 *       if (isTerminalTaskStatus(task.status)) void refreshWorkspace();
 *     },
 *   });
 */

import { useEffect, useRef } from 'react';
import { useTaskStore } from '../store/taskStore';
import { TERMINAL_STATUSES } from '../types';
import type { BackendTask, TaskStatus } from '../types';

export interface UseTaskTransitionOptions {
  /** Return true for tasks this surface cares about. */
  filter: (task: BackendTask) => boolean;
  /** Called once per status change of a matching task. */
  onTransition: (task: BackendTask, previous: BackendTask | undefined) => void;
  /**
   * Also fire for matching tasks first seen after mount (previous === undefined).
   * Default false — note that the store's initial hydration after connect()
   * delivers every historical task as "first seen", so opting in means the
   * callback runs for pre-existing tasks right after a (re)connect.
   */
  fireOnAppear?: boolean;
}

/** Terminal-state detection helper (completed | error | cancelled | skipped). */
export function isTerminalTaskStatus(status: TaskStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export function useTaskTransition(options: UseTaskTransitionOptions): void {
  // Latest callbacks live in a ref so the store subscription arms exactly once.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    let prevTasks = useTaskStore.getState().tasks;

    const unsubscribe = useTaskStore.subscribe((state) => {
      const currTasks = state.tasks;
      // Store set() calls that didn't touch the task map (filter toggles etc.)
      if (currTasks === prevTasks) return;
      const prev = prevTasks;
      prevTasks = currTasks;

      const { filter, onTransition, fireOnAppear } = optionsRef.current;
      for (const taskId of Object.keys(currTasks)) {
        const task = currTasks[taskId];
        const prevTask = prev[taskId];
        // Identity preserved by the store ⇒ the doc did not change at all.
        if (prevTask === task) continue;
        if (prevTask === undefined) {
          if (!fireOnAppear) continue;
        } else if (prevTask.status === task.status) {
          // Heartbeat / metadata-only write — not a transition.
          continue;
        }
        if (!filter(task)) continue;
        onTransition(task, prevTask);
      }
    });

    return unsubscribe;
  }, []);
}
